import React, { memo, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import AccordionCDS from '@sharedCR/coreDesignSystem/AccordionCDS';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import Skeleton from 'app/shared/components/common/Skeleton';
import PropTypes from 'prop-types';

import { SKELETON_TYPES } from 'constants/skeleton';

import { SnapdocsClosingSectionTestIds } from '../../SnapdocsClosingSection.testIds';

import { useSnapdocsAuditTrail } from './hooks/useSnapdocsAuditTrail';
import { auditTrailTableConfig } from './SnapdocsAuditTrailBlock.config';
import {
  AUDIT_SKELETON_ROWS,
  AUDIT_TRAIL_EMPTY_MESSAGE,
  AUDIT_TRAIL_TITLE,
  AuditTrailLoadState,
} from './SnapdocsAuditTrailBlock.constants';
import { accordionStyles } from './SnapdocsAuditTrailBlock.styles';

const SnapdocsAuditTrailBlock = ({ applicationId, companyId }) => {
  const { loadState, events, errorMessage, load } = useSnapdocsAuditTrail({
    applicationId,
    companyId,
  });

  const onAccordionChange = useCallback(
    (_event, isExpanded) => {
      if (isExpanded && loadState === AuditTrailLoadState.IDLE) {
        load();
      }
    },
    [load, loadState],
  );

  const getContent = () => {
    if (loadState === AuditTrailLoadState.LOADING) {
      return (
        <Box data-testid={SnapdocsClosingSectionTestIds.AUDIT_TRAIL_LOADING}>
          <Skeleton height={32} length={AUDIT_SKELETON_ROWS} type={SKELETON_TYPES.LIST} />
        </Box>
      );
    }

    if (loadState === AuditTrailLoadState.ERROR) {
      return (
        <ErrorHandlingLayout
          isError
          dataTestId={SnapdocsClosingSectionTestIds.AUDIT_TRAIL_RETRY_BUTTON}
          onClick={load}
        >
          <Typography
            color="error.main"
            data-testid={SnapdocsClosingSectionTestIds.AUDIT_TRAIL_ERROR}
            variant="regular14"
          >
            {errorMessage}
          </Typography>
        </ErrorHandlingLayout>
      );
    }

    if (loadState === AuditTrailLoadState.LOADED && !events.length) {
      return (
        <Typography
          color="text.secondary"
          data-testid={SnapdocsClosingSectionTestIds.AUDIT_TRAIL_EMPTY}
          variant="regular14"
        >
          {AUDIT_TRAIL_EMPTY_MESSAGE}
        </Typography>
      );
    }

    return (
      <Box data-testid={SnapdocsClosingSectionTestIds.AUDIT_TRAIL_TABLE}>
        <TableCDS config={auditTrailTableConfig} data={events} />
      </Box>
    );
  };

  return (
    <Box data-testid={SnapdocsClosingSectionTestIds.AUDIT_TRAIL_BLOCK}>
      <AccordionCDS
        className=""
        customStyles={accordionStyles}
        title={AUDIT_TRAIL_TITLE}
        titleVariant="medium16"
        onChange={onAccordionChange}
      >
        {getContent()}
      </AccordionCDS>
    </Box>
  );
};

SnapdocsAuditTrailBlock.propTypes = {
  applicationId: PropTypes.string.isRequired,
  companyId: PropTypes.string,
};

export default memo(SnapdocsAuditTrailBlock);
